/**
 * v1 legacy preferences: separate GET/POST `allergies` and `dislikes` endpoints.
 * Used while the combined `allergy_dislikes` v3 picker is disabled.
 */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../../shared/auth/store';
import { fetchProfileAndSyncUser } from '../profile/api';
import { shouldRetryPreferencesQuery } from '../../../platform/support/issueReport';
import { getAllergyPickerMode } from '../config/appConfig';
import { keys } from '../../query/keyRegistry';
import {
  fetchAllergies,
  fetchDislikes,
  saveAllergies,
  saveDislikes,
  type AllergiesResponse,
  type DislikesResponse,
} from './api';

export const LEGACY_ALLERGIES_QUERY_KEY = ['preferences', 'allergies'] as const;
export const LEGACY_DISLIKES_QUERY_KEY = ['preferences', 'dislikes'] as const;
const PROFILE_ALLERGIES_DISLIKES_QUERY_KEY = ['profile', 'allergies-dislikes'] as const;

const preferences_query_retry = (failure_count: number, error: unknown) =>
  shouldRetryPreferencesQuery(failure_count, error);

function parseNumericId(x: unknown): number | null {
  if (typeof x === 'number' && Number.isFinite(x)) {
    return x;
  }
  if (typeof x === 'string') {
    const n = Number(x);
    if (Number.isFinite(n)) {
      return n;
    }
  }
  return null;
}

function normalizeIdList(raw: unknown[]): number[] {
  const out: number[] = [];
  for (const x of raw) {
    if (x != null && typeof x === 'object') {
      const row = x as { id?: unknown; allergy_id?: unknown; dislike_id?: unknown; inventory_id?: unknown };
      const n =
        parseNumericId(row.inventory_id) ??
        parseNumericId(row.id) ??
        parseNumericId(row.allergy_id) ??
        parseNumericId(row.dislike_id);
      if (n != null) {
        out.push(n);
      }
      continue;
    }
    const n = parseNumericId(x);
    if (n != null) {
      out.push(n);
    }
  }
  return out;
}

function allergyIdsFromProfile(user: { allergies?: unknown[]; allergy_ids?: number[] } | null): number[] {
  if (!user) {
    return [];
  }
  if (Array.isArray(user.allergy_ids) && user.allergy_ids.length > 0) {
    return normalizeIdList(user.allergy_ids);
  }
  if (!Array.isArray(user.allergies)) {
    return [];
  }
  const mapped = user.allergies.map((a: unknown) => {
    if (typeof a === 'number') {
      return a;
    }
    if (a != null && typeof a === 'object') {
      const row = a as { id?: number; allergy_id?: number };
      return row.id ?? row.allergy_id;
    }
    return null;
  });
  return normalizeIdList(mapped);
}

function dislikeIdsFromProfile(user: { dislikes?: unknown[]; dislike_ids?: number[] } | null): number[] {
  if (!user) {
    return [];
  }
  if (Array.isArray(user.dislike_ids) && user.dislike_ids.length > 0) {
    return normalizeIdList(user.dislike_ids);
  }
  if (!Array.isArray(user.dislikes)) {
    return [];
  }
  const mapped = user.dislikes.map((d: unknown) => {
    if (typeof d === 'number') {
      return d;
    }
    if (d != null && typeof d === 'object') {
      const row = d as { id?: number; dislike_id?: number };
      return row.id ?? row.dislike_id;
    }
    return null;
  });
  return normalizeIdList(mapped);
}

function selectedAllergyIdsFromGet(data: AllergiesResponse | undefined): number[] | null {
  if (data == null) {
    return null;
  }
  const top = data.customer_selected_allergy_ids;
  const nested =
    data.data?.customer_selected_allergy_ids ?? data.data?.customer_selected_allergies;
  if (Array.isArray(top)) {
    return normalizeIdList(top);
  }
  if (Array.isArray(nested)) {
    return normalizeIdList(nested);
  }
  return null;
}

function selectedDislikeIdsFromGet(data: DislikesResponse | undefined): number[] | null {
  if (data == null) {
    return null;
  }
  const top = data.customer_selected_dislike_ids;
  const nested =
    data.data?.customer_selected_dislike_ids ?? data.data?.customer_selected_dislikes;
  if (Array.isArray(top)) {
    return normalizeIdList(top);
  }
  if (Array.isArray(nested)) {
    return normalizeIdList(nested);
  }
  return null;
}

function isSplitAllergyPickerMode(): boolean {
  return getAllergyPickerMode() === 'split';
}

export function useAllergiesQuery(customer_id: number | undefined) {
  return useQuery({
    queryKey: [...LEGACY_ALLERGIES_QUERY_KEY, customer_id],
    queryFn: () => fetchAllergies(customer_id!),
    enabled: typeof customer_id === 'number' && isSplitAllergyPickerMode(),
    retry: preferences_query_retry,
  });
}

export function useDislikesQuery(customer_id: number | undefined) {
  return useQuery({
    queryKey: [...LEGACY_DISLIKES_QUERY_KEY, customer_id],
    queryFn: () => fetchDislikes(customer_id!),
    enabled: typeof customer_id === 'number' && isSplitAllergyPickerMode(),
    retry: preferences_query_retry,
  });
}

export function useSaveAllergiesMutation() {
  const query_client = useQueryClient();
  const customer_id = useAuthStore(s => s.user?.id);

  return useMutation({
    mutationFn: (allergy_ids: number[]) => saveAllergies(customer_id!, allergy_ids),
    onSuccess: async () => {
      query_client.invalidateQueries({ queryKey: LEGACY_ALLERGIES_QUERY_KEY });
      query_client.invalidateQueries({ queryKey: ['profile'] });
      query_client.invalidateQueries({ queryKey: PROFILE_ALLERGIES_DISLIKES_QUERY_KEY });
      if (typeof customer_id === 'number') {
        query_client.invalidateQueries({ queryKey: keys.customer(customer_id).preferences });
        await fetchProfileAndSyncUser(customer_id).catch(() => {});
      }
    },
  });
}

export function useSaveDislikesMutation() {
  const query_client = useQueryClient();
  const customer_id = useAuthStore(s => s.user?.id);

  return useMutation({
    mutationFn: (dislike_ids: number[]) => saveDislikes(customer_id!, dislike_ids),
    onSuccess: async () => {
      query_client.invalidateQueries({ queryKey: LEGACY_DISLIKES_QUERY_KEY });
      query_client.invalidateQueries({ queryKey: ['profile'] });
      query_client.invalidateQueries({ queryKey: PROFILE_ALLERGIES_DISLIKES_QUERY_KEY });
      if (typeof customer_id === 'number') {
        query_client.invalidateQueries({ queryKey: keys.customer(customer_id).preferences });
        await fetchProfileAndSyncUser(customer_id).catch(() => {});
      }
    },
  });
}

export function useLegacySelectedAllergyIds(): number[] {
  const user = useAuthStore(s => s.user);
  const customer_id = user?.id;
  const { data: allergies_data, isSuccess: allergies_ok } = useAllergiesQuery(customer_id);

  return useMemo(() => {
    if (allergies_ok && typeof customer_id === 'number') {
      const from_api = selectedAllergyIdsFromGet(allergies_data);
      if (from_api !== null) {
        return from_api;
      }
    }
    return allergyIdsFromProfile(user);
  }, [user, allergies_ok, customer_id, allergies_data]);
}

export function useLegacySelectedDislikeIds(): number[] {
  const user = useAuthStore(s => s.user);
  const customer_id = user?.id;
  const { data: dislikes_data, isSuccess: dislikes_ok } = useDislikesQuery(customer_id);

  return useMemo(() => {
    if (dislikes_ok && typeof customer_id === 'number') {
      const from_api = selectedDislikeIdsFromGet(dislikes_data);
      if (from_api !== null) {
        return from_api;
      }
    }
    return dislikeIdsFromProfile(user);
  }, [user, dislikes_ok, customer_id, dislikes_data]);
}
